import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { IQuoteStatus, IQuoteableCarriersProduct } from '@boldpenguin/sdk-types';
import { IControlInputEvent, WebComponentInputBase } from 'bqx-application-web-components';
import { Actions } from 'bqx-deal';
import { LogLevel, LoggerAbstractService } from 'bqx-logging';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Observable, ReplaySubject, Subject, of } from 'rxjs';
import { catchError, filter, finalize, map, pairwise, startWith, switchMap } from 'rxjs/operators';
import { ApplicationQuestionSetCodes, SDKSessionItem } from 'src/app/enums/sdk-application.enum';
import { IApplication, IQuestionSet } from 'src/app/models/application-form/application-form.model';
import { Section } from 'src/app/models/shared/section.model';
import { ApplicationFormAbstractService } from 'src/app/services/application-form/application-form.abstract.service';
import { AbstractDealAnalyticsService } from 'src/app/services/deal-analytics/abstract-deal-analytics.service';
import { InputFocusAbstractService } from 'src/app/services/input-focus/input-focus.abstract.service';
@Component({
  selector: 'appform-sdk-question-set',
  templateUrl: './sdk-question-set.component.html',
  styleUrls: ['./sdk-question-set.component.css']
})
export class SdkQuestionSetComponent implements OnInit, OnChanges {
  @Input() activeMenuOrder;
  @Input() activeQuestionSet: IQuestionSet;
  private _bindQuoteUnsub$ = new Subject<any>();
  private _sdkInitData: any;
  private _readOnlyMode$ = new BehaviorSubject<boolean>(false);
  private _appFormState$ = new BehaviorSubject<string>(null);
  private _isQueSetLoading$ = new BehaviorSubject<boolean>(false);
  private _isAppFormLoading$ = new BehaviorSubject<boolean>(false);
  private _isSyncedWithBP$ = new BehaviorSubject<boolean>(false);
  private _areQuotesLoading$ = new BehaviorSubject<boolean>(false);
  private _isBridgingToSDKQSet$ = new Observable<boolean>();
  private requestWindow: Window | null;
  private _activeMenuOrder$ = new ReplaySubject<number>();
  private _activeQuestSetCode$ = new ReplaySubject<string>();

  @Input() formData: IApplication;
  public get sdkInitData(): any {
    return this._sdkInitData;
  }
  //This property is mapped to BQX Core as Input property to web components.
  //Keeping this as BehaviorSubject for now, will change this to Observable once changed in BQX Core.
  public get readOnlyMode$(): BehaviorSubject<boolean> {
    return this._readOnlyMode$;
  }

  //This property is mapped to BQX Core as Input property to web components.
  //Keeping this as BehaviorSubject for now, will change this to Observable once changed in BQX Core.
  public get appFormState$(): BehaviorSubject<string> {
    return this._appFormState$;
  }

  public get isBridgingToSDKQSet$(): Observable<boolean> {
    return this._isBridgingToSDKQSet$;
  }

  public get isQueSetLoading$(): Observable<boolean> {
    return this._isQueSetLoading$.asObservable();
  }

  public get isAppFormLoading$(): Observable<boolean> {
    return this._isAppFormLoading$.asObservable();
  }

  public get isSyncedWithBP$(): Observable<boolean> {
    return this._isSyncedWithBP$.asObservable();
  }

  public get areQuotesLoading$(): Observable<boolean> {
    return this._areQuotesLoading$.asObservable();
  }

  public get isEOMLoading$(): Observable<boolean> {
    return this.applicationFormService.isEOMLoading$;
  }
  public get activeMenuOrder$(): Observable<number> {
    return this._activeMenuOrder$.asObservable();
  }
  public get carrierProducts$(): Observable<IQuoteableCarriersProduct []> {
    return this.applicationFormService.carrierProducts$;
  }
  public get isOwner$(): Observable<boolean> {
    return this.applicationFormService.isOwner$;
  }

  public get isReferred(): boolean {
    return this.applicationFormService.isReferred;
  }
  constructor(
    private applicationFormService: ApplicationFormAbstractService,
    private focus: InputFocusAbstractService,
    private loggerService: LoggerAbstractService,
    private logger: NGXLogger,
    private dealAnalyticsService: AbstractDealAnalyticsService
  ) {
    this.setDealPageEventChanges();
  }

  private setDealPageEventChanges(): void {
    const _valueChanges$ = this._activeQuestSetCode$.pipe(
      startWith(''),
      pairwise(),
      filter(([prev, next]) => prev != next)
    );
    _valueChanges$.subscribe(([prev, next]) => {
      switch (this.activeMenuOrder){
        case 0: // Rates page
          this.dealAnalyticsService.postDealPageEvent('/application_form', 'Carrier Results');
          break;
        case 5: { // MQS page
          const questionSet = this.applicationFormService.activeQuestionSet$.value?.question_set;
          const mqsPageTitle = questionSet?.code.includes('mqs') ? 'MQS-' + questionSet?.title : questionSet?.title;
          this.dealAnalyticsService.postDealPageEvent('/application_form', mqsPageTitle);
          break;
        }
        default:
          if(this.applicationFormService.quoteNumber){
            //if logic needed to prevent extra deal call
            this.dealAnalyticsService.postDealPageEvent(null, null);
          }
          break;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.keys(changes).some((k) => k === 'activeQuestionSet')) {
      if (this.formData?.form?.reference_id) {
        this._activeQuestSetCode$.next(this.activeQuestionSet?.question_set?.code);
      }
    }
    if (Object.keys(changes).some((k) => k === 'activeMenuOrder')) {
      if (this.formData?.form?.reference_id) {
        if (changes.activeMenuOrder.currentValue >= 0) {
          this._activeMenuOrder$.next(changes.activeMenuOrder.currentValue);
        }
      }
    }
  }

  ngOnInit(): void {
    this.initWebComponentData();
    if (!sessionStorage.getItem(SDKSessionItem.UID_TOKEN) && this._appFormState$.getValue() === null)
      this.applicationFormService.loadSdkApplication();
    this._readOnlyMode$ = this.applicationFormService.readOnlyMode$;
    this._appFormState$ = this.applicationFormService.appFormState$;
    this._isQueSetLoading$ = this.applicationFormService.isActiveQuestionSetLoading$;
    this._isAppFormLoading$ = this.applicationFormService.isSDKAppFormLoading$;
    this._isSyncedWithBP$ = this.applicationFormService.isAppSyncedWithBP$;
    this._areQuotesLoading$ = this.applicationFormService.areQuotesLoading$;
    this._isBridgingToSDKQSet$ = this.applicationFormService.isBridgingToSDKQSet$;
    this._activeMenuOrder$.next(this.activeMenuOrder === undefined || 0 ? 0 : this.activeMenuOrder);
  }

  private initWebComponentData() {
    this._sdkInitData = {
      formControlProperties: {
        controls: {
          'bp-save-button': {
            properties: {
              text: 'Next',
              loadingStateText: 'Saving...'
            }
          },
          'bp-quote-request': {
            properties: {
              isQuoteRequestAsDialog: true
            }
          },
          'bp-base-input': {
            properties: {
              helpButtonText: '?',
              helpButtonTitle: 'more info',
              helpButtonPopeOverTitle: 'More Info:',
              helpButtonPlacement: 'top-left',
              optionalTag: '(Optional)',
              helpButtonDisabledFocus: true
            }
          }
        }
      }
    };
  }

  public onInputValueChange(event: IControlInputEvent) {
    if (this.isAnalyticsChangeAllowed(event.input)) {
      const section: Section = this.dealAnalyticsService.currentSection;
      this.dealAnalyticsService.postDealControlEvent(
        this.getAnalyticsAction(event.input),
        event.input.label, 
        section.route, 
        section.title, 
        section.carrierName,
        this.getAnalyticDataValue(event.input)
      );
    }
  }

  public onSaveButtonClicked(event) {
    this.dealCallOnSaveClick();
    this.applicationFormService.subscribeControlToFocus();
  }

  public onSaveButtonBlurred(event) {
    //TODO: custom events
  }

  public onSaveButtonFocused(event) {
    //TODO: custom events
  }

  public onInputInit(): void {
    // Added this check to restrict focus emit on read only mode.
    if (!this.readOnlyMode$.getValue()) this.focus.emitInputIsSet();
  }

  public onQuoteBindCalled(event): void {
    let bindUrl = '';
    const data = event.data;
    const dealLabel = data.sourceView? 'Bind Policy - View Details' : 'Bind Policy';
    this.dealAnalyticsService.postDealControlEvent(Actions.ButtonClick,
      dealLabel,
      '/application_form',
      'Carrier Results',
      data?.carrierName);
    this.applicationFormService
      .bindQuote(data.appFormId, data.quoteId)
      .pipe(
        switchMap((url) => {
          if (url) {
            bindUrl = url;
            this.requestWindow = window.open(url, '_blank');
          }
          return this.applicationFormService.quoteRequests$.getValue();
        }),
        catchError((err: any, caught: Observable<any>) => {
          bindUrl = 'bindQuote() failed';
          this._bindQuoteUnsub$.next();
          this._bindQuoteUnsub$.complete();

          this.loggerService.postLogData(LogLevel.Error, 'sdk-question-set:onQuoteBindCalled', err, '');
          return of([]);
        }),
        map((quoteRequest) => {
            if (quoteRequest.selected) {
              this.InvokeCarrierDetailDealEvent(quoteRequest.carrier.name, 'Selected Carrier', null);
            }
        }),
        finalize(() => {
          this.loggerService.postLogData(LogLevel.Info, 'Bind Policy Attempted',
              JSON.stringify({quoteNumber: this.applicationFormService.quoteNumber, carrier: data?.carrierName, result: bindUrl}));
        })
      )
      .subscribe();
  }

  public onExploreMarketsClicked(event): void {
    this.dealAnalyticsService.postDealControlEvent(Actions.ButtonClick, 'Explore Other Markets', '/application_form', 'Carrier Results');
    this.applicationFormService.exploreOtherMarkets();
    sessionStorage.setItem('EOM', 'Y');
  }

  public onQuoteViewDetailsClicked(event): void {
    const data = event.data;
    this.dealAnalyticsService.postDealControlEvent(Actions.LinkClick,
      'View Details',
      '/application_form',
      'Carrier Results',
      data?.carrierName);
  }

  public onQuoteRequestView(event): void {
    const data = event.data;
    const quoteStatus: IQuoteStatus = data.quoteStatus;
    this.InvokeCarrierDetailDealEvent(data.carrierName, 'Status', data.requestStatus);
    this.InvokeCarrierDetailDealEvent(data.carrierName, 'RateReturned', null, data.quoteInfo?.annual_price);
    if( quoteStatus.recommended) {
      this.InvokeCarrierDetailDealEvent(data.carrierName, 'Reccomended Carrier', null);
    }
    if( quoteStatus.selected) {
      this.InvokeCarrierDetailDealEvent(data.carrierName, 'Selected Carrier', null);
    }
    if( quoteStatus.consumer_selected) {
      this.InvokeCarrierDetailDealEvent(data.carrierName, 'Consumer Selected Carrier', null);
    }
  }

  private InvokeCarrierDetailDealEvent(carrierName: string, label: string, dataValue: string, premiumAmount?: string) {
    this.dealAnalyticsService.postDealControlEvent(Actions.Message_Displayed,
      label,
      '/application_form',
      'Carrier Results',
      carrierName,
      dataValue,
      premiumAmount);
  }

  private dealCallOnSaveClick() {
    const currentQuestionSet: IQuestionSet = this.applicationFormService.activeQuestionSet$.value;
    if(currentQuestionSet?.question_set.code !== ApplicationQuestionSetCodes.CONSENT) {
      const section: Section =  this.dealAnalyticsService.currentSection;
      this.dealAnalyticsService.postDealControlEvent(Actions.ButtonClick, 'Next', section.route, section.title, section.carrierName);
    }
    else {
      const consentAnswer = currentQuestionSet?.answers[0];
      let selectedAnswer: string = 'Please select a valid answer';
      if(consentAnswer) {
        const selectedChoice = consentAnswer.question.choices?.find(choice => choice.id === consentAnswer.choice_id);
        if(selectedChoice) {
          selectedAnswer = selectedChoice.value;
        }
      }
      this.dealAnalyticsService.postDealControlEvent(Actions.ButtonClick, 'Next', null, null, null, selectedAnswer);
    }
  }

  private getAnalyticsAction(input: WebComponentInputBase): Actions {
    let action: Actions;
    switch (input.fieldType) {
      case 'dropdown':
        action = Actions.DropdownChange;
        break;
      case 'radio':
        action = Actions.RadioButtonChange;
        break;
      case 'checkbox':
      case 'groupcheckbox':
        action = Actions.CheckBoxChange;
        break;
      default:
        action = Actions.Change;
        break;
    }
    return action;
  }

  private isAnalyticsChangeAllowed(input: WebComponentInputBase): boolean {
    let allowed = false;
    switch (input.fieldType) {
      case 'radio':
        allowed = true;
        break;
      default:
        break;
    }
    return allowed;
  }

  private getAnalyticDataValue(input): string {
    let dataValue = '';
    switch (input.fieldType) {
      case 'dropdown':
        dataValue = input.choices.find((choice) => choice.id == input.inputControl.value)?.alias;
        break;
      case 'radio':
        dataValue = input.choices.find((choice) => choice.id == input.inputControl.value)?.value;
        break;
      case 'checkbox':
        dataValue = input.inputControl.value;
        break;
      case 'groupcheckbox':
        break;
      default:
        dataValue = input.inputControl.value;
        break;
    }
    return dataValue;
  }
}
